import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import PlacesAutocomplete, { geocodeByPlaceId, getLatLng } from 'react-places-autocomplete';

import classNames from 'classnames';
import Suggestion from './Suggestion';
import Input from './Input';
import Values from '../Values';

import styles from './styles.module.scss';
import { getAddressVerification, getPlaceAddressCASSAddressesAutocomplete } from 'api/address';
import { isState, showErrorMessage } from 'helpers';
import { getSchoolsDistrictsAutocompleteResults } from 'api/listingDetail';
import { useHistory } from 'react-router-dom';
import { CASS_ADDRESS } from 'settings/constants/locations';
import { LocalStorage } from 'services';

const autocompleteRequestSupportedTypes = [
  'geocode',
  'address',
  'establishment',
  '(regions)',
  '(cities)',
  'administrative_area_level_1',
  'administrative_area_level_2',
];

const GeoCoderComponent = forwardRef((props: any, ref: any) => {
  const {
    className,
    onResult,
    placeholder,
    countries,
    types,
    currentValue,
    onDeleteValue,
    inputClassName,
    inputIconClassName,
    onChange,
    variant,
    showPrefixIcon,
    dropdownClassName,
    searchWrapperClassName,
    menuTop,
    shouldFilterStateLevelResults,
    shouldFilterPlaces,
    shouldFilterCityLevelResults,
    shouldFilterStreetLevelResults,
    updateInput,
    resetUpdate,
    loaderClassName,
    showOnlyCassAddresses,
    showOnlyCityAndState,
    customLoading,
    setIsLoading,
  } = props;
  const [localValue, setLocalValue] = useState('');
  const [isPending, setIsPending] = useState(false);
  const [cassAddressSuggestions, setcassAddressSuggestion] = useState([]);
  const [schoolSuggestions, setSchoolSuggestions] = useState([]);
  const [placesSuggestions, setPlacesSuggestions] = useState([]);
  const [keydownPressed, setKeyDownPressed] = useState(false);
  const history = useHistory();

  const onSelectHandler = useCallback(
    (placeName, placeId, placeInfo) => {
      if (placeInfo?.ListingId) {
        LocalStorage.setSearchCriteria({});
        history.push(`/properties/search/listing-detail/${placeInfo?.Id}`);
      }
      const placeNameExists = currentValue.some(
        (currentVal) => currentVal?.PlaceName && currentVal.PlaceName === placeInfo?.street_line,
      );
      if (placeNameExists) {
        setSchoolSuggestions([]);
        setPlacesSuggestions([]);
        setcassAddressSuggestion([]);
        return;
      }
      if (placeId) {
        geocodeByPlaceId(placeId).then((res) => {
          getLatLng(res[0]).then((coordinates) => {
            onResult({ result: res, placeName, coordinates, placeInfo, type: 'google' });
          });
        });
      } else if (placeInfo) {
        if (placeInfo?.Type === 'District') {
          onResult({
            Type: 'SchoolDistrict',
            SchoolDistrictId: placeInfo?.Id,
            SchoolDistrictName: placeInfo?.Name,
          });
        }

        // For CASS Addresses
        if (!placeInfo?.ListingId && placeInfo?.Type !== 'District') {
          getAddressVerification(placeInfo).then((res: any) => {
            let coordinates = {
              lat: res?.data?.result[0]?.metadata?.latitude,
              lng: res?.data?.result[0]?.metadata?.longitude,
            };
            onResult({
              result: res?.data?.result[0],
              placeName,
              coordinates,
              placeInfo,
              type: CASS_ADDRESS,
            });
          });
        }
      }

      setLocalValue('');
      setSchoolSuggestions([]);
      setPlacesSuggestions([]);
      setcassAddressSuggestion([]);
    },
    [onResult, setLocalValue, schoolSuggestions],
  );

  const handleKeyDown = (event) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      setKeyDownPressed(true);
    } else {
      setKeyDownPressed(false);
    }
  };

  const onChangeHandler = useCallback(
    async (val) => {
      onChange && onChange(val);
      setLocalValue(val);
    },
    [onChange, setLocalValue],
  );

  useEffect(() => {
    const getData = setTimeout(async () => {
      if (!localValue) {
        setSchoolSuggestions([]);
        setPlacesSuggestions([]);
        setcassAddressSuggestion([]);
        return;
      }
      if (!keydownPressed) {
        setIsLoading(true);
        setIsPending(true);

        const [schoolsResult, placesAndCassResult] = await Promise.allSettled([
          getSchoolsDistrictsAutocompleteResults({ text: localValue, limit: 5 }),
          getPlaceAddressCASSAddressesAutocomplete(localValue),
        ]);
        if (schoolsResult.status === 'fulfilled') {
          setSchoolSuggestions(schoolsResult.value?.data?.result?.Items || []);
        } else {
          showErrorMessage(schoolsResult.reason);
        }
        if (placesAndCassResult.status === 'fulfilled') {
          setPlacesSuggestions(placesAndCassResult.value?.data?.result?.fullAddress || []);
          setcassAddressSuggestion(
            placesAndCassResult.value?.data?.result?.smarty?.suggestions || [],
          );
        } else {
          showErrorMessage(placesAndCassResult.reason);
        }
        setIsPending(false);
        setIsLoading(false);
      }
    }, 1000);
    return () => clearTimeout(getData);
  }, [localValue]);

  const searchTypes = useMemo(
    () => (types || []).filter((type) => autocompleteRequestSupportedTypes.includes(type)),
    [types],
  );

  const searchOptions = useMemo(
    () => ({
      componentRestrictions: { country: countries || ['us'] },
      language: 'en',
      types: searchTypes?.length ? [searchTypes[0]] : undefined,
    }),
    [countries, searchTypes],
  );

  const RenderSuggestions = ({
    googleSuggestions,
    placesSuggestions,
    schoolsSuggestions,
    getSuggestionItemProps,
  }) => {
    const [suggestions, setSuggestions] = useState([]);

    useEffect(() => {
      const fetchSuggestions = async () => {
        if (!googleSuggestions?.length) return;
        let suggestionsList = googleSuggestions;

        if (shouldFilterStreetLevelResults) {
          const streetLevelTypes = ['premise', 'subpremise', 'street_address'];
          suggestionsList = suggestionsList.filter(
            (suggestion) => !suggestion.types.some((type) => streetLevelTypes.includes(type)),
          );
        }

        //NOTE: Filter USA
        suggestionsList = suggestionsList.filter(({ description }) => description !== 'USA');

        if (shouldFilterPlaces) {
          suggestionsList = suggestionsList.filter(
            (suggestion) => !suggestion.types.includes('point_of_interest'),
          );
        }

        if (shouldFilterCityLevelResults || shouldFilterStateLevelResults) {
          suggestionsList = suggestionsList.filter(
            (suggestion) =>
              !suggestion.types.includes('point_of_interest') &&
              (shouldFilterCityLevelResults
                ? !(
                    suggestion.types.includes('administrative_area_level_1') ||
                    ['geocode', 'political', 'locality'].every((type) =>
                      suggestion.types.includes(type),
                    )
                  )
                : !suggestion.types.includes('administrative_area_level_1')),
          );
        }

        //If State ONLY Request, explicitly filter.  Other languages are included otherwise.
        if (types && types.length === 1 && types[0] === 'administrative_area_level_1') {
          suggestionsList = suggestionsList.filter((suggestion) =>
            isState(suggestion.terms[0]?.value),
          );
        }

        if (showOnlyCityAndState) {
          suggestionsList = suggestionsList.filter(
            (suggestion) =>
              (suggestion.types.includes('administrative_area_level_1') &&
                isState(suggestion?.terms?.[0]?.value)) ||
              (suggestion.types.includes('locality') &&
                !suggestion?.terms?.[0]?.value?.includes?.('City')),
          );
        }

        setSuggestions(suggestionsList);
      };
      fetchSuggestions();
    }, [googleSuggestions]);

    if (
      (!suggestions.length && !schoolsSuggestions.length && !placesSuggestions.length) ||
      isPending
    )
      return null;

    const LocationIcon = () => (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
      >
        <ellipse cx="10" cy="9.16699" rx="2.5" ry="2.5" stroke="#747475" strokeWidth="2" />
        <path
          d="M16.6663 9.16667C16.6663 12.8846 12.9802 16.3721 11.0899 17.9162C10.4484 18.4401 9.55094 18.4401 8.90949 17.9162C7.01913 16.3721 3.33301 12.8846 3.33301 9.16667C3.33301 5.48477 6.31778 2.5 9.99967 2.5C13.6816 2.5 16.6663 5.48477 16.6663 9.16667Z"
          stroke="#747475"
          strokeWidth="2"
        />
      </svg>
    );
    const AddressIcon = () => (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
      >
        <path
          d="M4.16699 8.33398V16.6673H15.8337V8.33398"
          stroke="#747475"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M8.33301 16.6667V12.5H11.6663V16.6667"
          stroke="#747475"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M2.91699 9.16732L10.0003 3.33398L17.0837 9.16732"
          stroke="#747475"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M15.833 8.33398V3.33398H13.333V5.83398"
          stroke="#747475"
          strokeWidth="2"
          strokeLinejoin="round"
        />
      </svg>
    );
    const SchoolIcon = () => (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
      >
        <path
          d="M15.625 8.75V12.3744C15.625 12.7274 15.4531 13.0642 15.1473 13.2697C11.7158 15.5768 8.28423 15.5768 4.85268 13.2697C4.54693 13.0642 4.375 12.7274 4.375 12.3744V8.75"
          stroke="#747475"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M1.58225 7.34003C1.13925 7.16481 1.13925 6.58519 1.58225 6.40997L9.78405 3.16591C9.92195 3.11136 10.078 3.11136 10.2159 3.16591L18.4177 6.40997C18.8607 6.58519 18.8607 7.16481 18.4177 7.34003L10.2159 10.5841C10.078 10.6386 9.92195 10.6386 9.78405 10.5841L1.58225 7.34003Z"
          stroke="#747475"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path d="M2.1875 7.8125V14.375" stroke="#747475" strokeWidth="2" />
        <path
          d="M2.8125 15.9375L3.125 18.3689C2.58929 18.877 1.78571 18.877 1.25 18.3689L1.5625 15.9375"
          stroke="#747475"
          strokeWidth="2"
          strokeLinejoin="round"
        />
        <path
          d="M3.125 15.3125C3.125 15.8303 2.70527 16.25 2.1875 16.25C1.66973 16.25 1.25 15.8303 1.25 15.3125C1.25 14.7947 1.66973 14.375 2.1875 14.375C2.70527 14.375 3.125 14.7947 3.125 15.3125Z"
          stroke="#747475"
          strokeWidth="2"
        />
      </svg>
    );

    return (
      <div testid="suggestions" className={classNames('suggestions', styles.suggestions)}>
        {[
          ...(suggestions?.length ? [{ suggestions, label: 'Places', Icon: LocationIcon }] : []),
          ...(placesSuggestions?.length
            ? [{ suggestions: placesSuggestions, label: 'Addresses', Icon: AddressIcon }]
            : []),
          ...(schoolsSuggestions?.length
            ? [{ suggestions: schoolsSuggestions, label: 'School Districts', Icon: SchoolIcon }]
            : []),
        ]?.map(({ suggestions, label, Icon }) => (
          <div className={styles.wrapper}>
            <div
              testid="searchResults"
              className={classNames('searchResults', styles.searchResults)}
            >
              <span className={styles.icon}>
                <Icon />
              </span>
              {label}
            </div>
            {suggestions.map((suggestion, i) => (
              <Suggestion
                key={i}
                className={classNames('suggestionItem', styles.suggestion)}
                getSuggestionItemProps={getSuggestionItemProps}
                suggestion={suggestion}
                formatStateSuggestion={
                  types?.includes('administrative_area_level_1') ||
                  suggestion.types?.includes('postal_code')
                }
              />
            ))}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={classNames(styles.placesAutocompleteWrapper, searchWrapperClassName)}>
      <div
        testid="geocoder"
        ref={ref}
        className={classNames(styles.wrapper, styles[variant], className)}
      >
        <PlacesAutocomplete
          value={localValue}
          debounce={1000}
          onChange={onChangeHandler}
          onSelect={onSelectHandler as any}
          searchOptions={searchOptions}
        >
          {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => {
            if (suggestions?.length && updateInput?.length) {
              onSelectHandler(suggestions[0]?.description, suggestions[0]?.placeId, suggestions[0]);
              resetUpdate();
            }

            return (
              <>
                <Input
                  getInputProps={getInputProps}
                  className={classNames(styles.baseLocationInput, inputClassName)}
                  iconClassName={classNames(inputIconClassName)}
                  placeholder={placeholder}
                  loading={loading || isPending || customLoading}
                  variant={variant}
                  showPrefixIcon={showPrefixIcon}
                  onKeyDown={handleKeyDown}
                  loaderClassName={loaderClassName}
                />
                <div
                  className={classNames(
                    styles.dropdownWrapper,
                    { [styles.menuTop]: menuTop },
                    dropdownClassName,
                  )}
                >
                  <Values
                    values={currentValue}
                    onDelete={onDeleteValue}
                    className={styles.values}
                    displayedValuesLimit={11}
                    multiple={false}
                    groupSize={2}
                  />
                  <RenderSuggestions
                    googleSuggestions={suggestions}
                    schoolsSuggestions={schoolSuggestions}
                    placesSuggestions={
                      currentValue?.length === 0 // If there's no value selected, then cass addresses can be part of suggestions.
                        ? [...placesSuggestions, ...cassAddressSuggestions]
                        : placesSuggestions
                    }
                    getSuggestionItemProps={getSuggestionItemProps}
                  />
                </div>
              </>
            );
          }}
        </PlacesAutocomplete>
      </div>
    </div>
  );
});

export default GeoCoderComponent;
